import React, { useState, useEffect } from 'react';
import './SeasonScroll.css';
import '../../styles/theme.css';

interface SeasonScrollProps {
    seasons: number[];
    selectedSeason: number;
    onSeasonChange: (season: number) => void;
    isLoading?: boolean;
}

const SeasonScroll: React.FC<SeasonScrollProps> = ({
    seasons,
    selectedSeason,
    onSeasonChange,
    isLoading = false
}) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Sort seasons in descending order
    const sortedSeasons = [...seasons].sort((a, b) => b - a);

    // On mobile, show only 3 most recent seasons + dropdown for rest
    const visibleCount = isMobile ? 3 : sortedSeasons.length;
    const visibleSeasons = sortedSeasons.slice(0, visibleCount);
    const dropdownSeasons = isMobile ? sortedSeasons.slice(visibleCount) : [];

    // If selected season is in dropdown, show it in visible area instead of the last visible one
    const selectedInDropdown = dropdownSeasons.includes(selectedSeason);

    const buttonStyle = (season: number) => ({
        backgroundColor: season === selectedSeason
            ? '#ff6600'
            : 'rgba(255, 255, 255, 0.05)',
        color: season === selectedSeason
            ? '#ffffff'
            : 'rgba(255, 255, 255, 0.8)',
        border: season === selectedSeason
            ? '1px solid #ff6600'
            : '1px solid rgba(255, 102, 0, 0.2)',
        borderRadius: '6px',
        fontWeight: season === selectedSeason ? '600' : '400',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        opacity: isLoading && season === selectedSeason ? 0.8 : 1
    });

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>, season: number) => {
        if (!isLoading && season !== selectedSeason) {
            e.currentTarget.style.backgroundColor = 'rgba(255, 102, 0, 0.1)';
            e.currentTarget.style.borderColor = 'rgba(255, 102, 0, 0.3)';
        }
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>, season: number) => {
        if (!isLoading && season !== selectedSeason) {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.borderColor = 'rgba(255, 102, 0, 0.2)';
        }
    };

    return (
        <div className="navlist d-flex align-items-center flex-wrap gap-2">
            <div className="seasons-label">
                <span>Sæsoner</span>
            </div>
            <div className="d-flex align-items-center gap-1 flex-wrap">
                {visibleSeasons.map((season) => (
                    <button
                        key={season}
                        className={`nav-link season-button ${season === selectedSeason ? 'active' : ''}`}
                        type="button"
                        role="tab"
                        aria-selected={season === selectedSeason}
                        onClick={() => onSeasonChange(season)}
                        disabled={isLoading}
                        style={buttonStyle(season)}
                        onMouseEnter={(e) => handleMouseEnter(e, season)}
                        onMouseLeave={(e) => handleMouseLeave(e, season)}
                    >
                        <span className="season-number">{season}</span>
                        {isLoading && season === selectedSeason && (
                            <span
                                className="spinner-border spinner-border-sm button-spinner"
                                role="status"
                                aria-hidden="true"
                            ></span>
                        )}
                    </button>
                ))}

                {/* Dropdown for older seasons on mobile */}
                {dropdownSeasons.length > 0 && (
                    <div className="dropdown">
                        <button
                            className={`nav-link season-button season-dropdown-btn ${selectedInDropdown ? 'active' : ''}`}
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            style={buttonStyle(selectedInDropdown ? selectedSeason : -1)}
                        >
                            {selectedInDropdown ? selectedSeason : '...'}
                        </button>
                        <ul className="dropdown-menu dropdown-menu-dark">
                            {dropdownSeasons.map((season) => (
                                <li key={season}>
                                    <button
                                        className={`dropdown-item ${season === selectedSeason ? 'active' : ''}`}
                                        onClick={() => onSeasonChange(season)}
                                        disabled={isLoading}
                                    >
                                        Sæson {season}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SeasonScroll;